import type { ReactNode } from "react";
import { cn } from "../lib/utils.js";

export function Table({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className="w-full overflow-x-auto border border-slate-200 rounded-md shadow-sm bg-white">
      <table className={cn("min-w-full border-collapse align-middle text-left", className)}>
        {children}
      </table>
    </div>
  );
}

export function THead({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <thead className={cn("bg-slate-50 border-b border-slate-200", className)}>{children}</thead>
  );
}

export function TBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <tbody className={cn("divide-y divide-slate-100 bg-white", className)}>{children}</tbody>;
}

export function Th({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <th
      className={cn(
        "px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 select-none",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  colSpan,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Native HTML colSpan — forwarded straight to the underlying td.
   * Used by expandable rows (e.g. ModelPricesPage history panel) to
   * span the full table width without restating the column count
   * everywhere the table is consumed.
   */
  colSpan?: number;
}): JSX.Element {
  return (
    <td
      colSpan={colSpan}
      className={cn("px-4 py-2 text-[13px] text-slate-700 font-normal leading-normal", className)}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  /** Optional row click handler (e.g. select-row-to-open-detail). */
  onClick?: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onClick}
      className={cn("hover:bg-slate-50/60 transition-colors duration-75", className)}
    >
      {children}
    </tr>
  );
}
